import { useQuery } from '@tanstack/react-query';
import { backendClient } from '@/lib/backendClient';
import { useUserRole } from '@/hooks/useUserRole';

export interface AdminCreditOverview {
  totalPractitioners: number;
  totalCreditsInCirculation: number;
  totalCreditsUsed: number;
  totalCreditsPurchased: number;
  practitionersWithLowCredits: number;
  practitionersOutOfCredits: number;
}

export interface PractitionerCreditSummary {
  practitioner_id: string;
  practitioner_email: string;
  practitioner_name: string;
  credits_balance: number;
  total_credits_purchased: number;
  total_credits_used: number;
  last_transaction_date: string | null;
  status: 'active' | 'low' | 'depleted';
}

// Stable empty array to prevent infinite re-renders
const EMPTY_SUMMARIES: PractitionerCreditSummary[] = [];

export const useAdminCreditOverview = () => {
  const { hasAdminAccess } = useUserRole();

  const {
    data: overview,
    isLoading: overviewLoading,
    error: overviewError,
    refetch: refetchOverview
  } = useQuery({
    queryKey: ['admin-credit-overview'],
    queryFn: async (): Promise<AdminCreditOverview> => {
      const { data: credits, error } = await backendClient
        .from('user_credits')
        .select('*');

      if (error) throw error;

      const totalPractitioners = credits.length;
      const totalCreditsInCirculation = credits.reduce((sum, c) => sum + c.credits_balance, 0);
      const totalCreditsUsed = credits.reduce((sum, c) => sum + c.total_credits_used, 0);
      const totalCreditsPurchased = credits.reduce((sum, c) => sum + c.total_credits_purchased, 0);
      const practitionersWithLowCredits = credits.filter(c => c.credits_balance > 0 && c.credits_balance < 10).length;
      const practitionersOutOfCredits = credits.filter(c => c.credits_balance <= 0).length;

      return {
        totalPractitioners,
        totalCreditsInCirculation,
        totalCreditsUsed,
        totalCreditsPurchased,
        practitionersWithLowCredits,
        practitionersOutOfCredits,
      };
    },
    enabled: hasAdminAccess,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  const {
    data: practitionerSummaries = EMPTY_SUMMARIES,
    isLoading: summariesLoading,
    error: summariesError
  } = useQuery({
    queryKey: ['admin-practitioner-credit-summaries'],
    queryFn: async (): Promise<PractitionerCreditSummary[]> => {
      // Get user credits with profile data
      const { data, error } = await backendClient
        .from('user_credits')
        .select('*');

      if (error) throw error;

      // Get all profiles for user info
      const { data: profiles, error: profileError } = await backendClient
        .from('profiles')
        .select('id, email, first_name, last_name');

      if (profileError) throw profileError;

      // Create profile lookup map
      const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);

      // Get latest transaction date for each user
      const { data: latestTransactions, error: transError } = await backendClient
        .from('credit_transactions')
        .select('practitioner_id, created_at')
        .order('created_at', { ascending: false });

      if (transError) throw transError;

      const latestTransactionMap = new Map();
      latestTransactions?.forEach(trans => {
        if (!latestTransactionMap.has(trans.practitioner_id)) {
          latestTransactionMap.set(trans.practitioner_id, trans.created_at);
        }
      });

      return data.map((credits: any) => {
        const profile = profileMap.get(credits.user_id);
        const practitioner_name = profile
          ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'Unknown'
          : 'Unknown';

        let status: 'active' | 'low' | 'depleted' = 'active';
        if (credits.credits_balance <= 0) status = 'depleted';
        else if (credits.credits_balance < 10) status = 'low';

        return {
          practitioner_id: credits.user_id,
          practitioner_email: profile?.email || 'Unknown',
          practitioner_name,
          credits_balance: credits.credits_balance,
          total_credits_purchased: credits.total_credits_purchased,
          total_credits_used: credits.total_credits_used,
          last_transaction_date: latestTransactionMap.get(credits.user_id) || null,
          status,
        };
      });
    },
    enabled: hasAdminAccess,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  return {
    overview,
    practitionerSummaries,
    isLoading: overviewLoading || summariesLoading,
    error: overviewError || summariesError,
    refetchOverview,
  };
};
